declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

export interface FacebookUser {
  id: string;
  name: string;
  email: string;
  accessToken: string;
}

class FacebookService {
  private appId: string;
  private isInitialized: boolean = false;

  constructor() {
    // Replace with your Facebook App ID
    this.appId =  process.env.REACT_APP_FACEBOOK_APP_ID || '';
  }

  async init(): Promise<void> {
    if (this.isInitialized) return;

    console.log('Initializing Facebook SDK with appId:', this.appId);

    return new Promise((resolve) => {
      const performInit = () => {
        if (this.isInitialized) {
          resolve();
          return;
        }
        console.log('Calling FB.init with appId:', this.appId, 'and version v24.0');
        window.FB.init({
          appId: this.appId,
          cookie: true,
          xfbml: true,
          version: 'v24.0'
        });
        this.isInitialized = true;
        console.log('Facebook SDK initialized successfully');
        resolve();
      };

      if (window.FB) {
        // SDK script already loaded
        performInit();
        return;
      }

      // Hook into fbAsyncInit to init when SDK loads
      const previousFbAsyncInit = window.fbAsyncInit;
      window.fbAsyncInit = () => {
        if (previousFbAsyncInit) previousFbAsyncInit();
        performInit();
      };
    });
  }

  async login(): Promise<FacebookUser> {
    console.log('Starting Facebook login...');
    await this.init();

    return new Promise((resolve, reject) => {
      console.log('Calling FB.login...');
      const readOnlyScopes = [
        // Pages (read-only)
        'pages_show_list',
        // 'pages_read_engagement',  // read posts & comments
        'pages_read_user_content',// access Page posts, videos, reels
        // Instagram (read-only)
        'instagram_basic',
        'instagram_manage_insights',
      ].join(',');

      window.FB.login((response: any) => {
        console.log('[META_ASSETS][fe] FB.login callback', {
          status: response?.status,
          grantedScopes: response?.authResponse?.grantedScopes,
          hasAccessToken: !!response?.authResponse?.accessToken,
        });
        if (response.authResponse) {
          console.log('Login successful, getting user info...');
          // Get user info
          window.FB.api('/me', { fields: 'id,name,email' }, (userInfo: any) => {
            console.log('User info response:', userInfo);
            
            if (userInfo && !userInfo.error) {
              // Log granted permissions for verification (read-only expected)
              resolve({
                id: userInfo.id,
                name: userInfo.name,
                email: userInfo.email,
                accessToken: response.authResponse.accessToken
              });
            } else {
              console.error('Error getting user info:', userInfo);
              reject(new Error('Error getting user info'));
            }
          });
        } else {
          console.error('Facebook login failed:', response);
          reject(new Error('Facebook login failed'));
        }
      }, { scope: readOnlyScopes, auth_type: 'rerequest', return_scopes: true, config_id: "1367570675030485" });
    });
  }

  async logout(): Promise<void> {
    await this.init();

    return new Promise((resolve) => {
      window.FB.logout(() => {
        resolve();
      });
    });
  }

  getLoginStatus(): Promise<any> {
    return new Promise((resolve) => {
      window.FB.getLoginStatus((response: any) => {
        resolve(response);
      });
    });
  }
}

export const facebookService = new FacebookService();
